import { useEffect, useRef, useState } from "react";
import Shell from "@/components/Shell";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Search, KeyRound, UserCog, UserX, UserCheck, FileSpreadsheet, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 20;

const empty = { emp_code: "", name: "", password: "", email: "", mobile: "", region: "", role: "mr" };

export default function AdminEmployees() {
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const load = (pg = page) => {
    const params = new URLSearchParams({ page: pg, page_size: PAGE_SIZE });
    if (q) params.set("q", q);
    api.get(`/admin/employees?${params}`).then(({ data }) => {
      setEmployees(data.data);
      setTotal(data.total);
    });
  };

  useEffect(() => { load(page); }, [page, q]);

  const onQChange = (e) => { setQ(e.target.value); setPage(1); };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/employees/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Import complete: ${data.created} added, ${data.skipped} skipped.`);
      if (data.errors?.length) toast.error(`${data.errors.length} error(s): ${data.errors[0]}`);
      setPage(1); load(1);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setImporting(false);
    }
  };

  const create = async () => {
    try {
      await api.post("/admin/employees", form);
      toast.success("Employee added");
      setAdding(false); setForm(empty); setPage(1); load(1);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const save = async () => {
    try {
      const { id, emp_code, password_hash, created_at, poster_count, ...rest } = editing;
      await api.patch(`/admin/employees/${id}`, rest);
      toast.success("Updated");
      setEditing(null); load(page);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const reset = async () => {
    try {
      await api.post(`/admin/employees/${resetting.id}/reset-password`, { new_password: newPwd });
      toast.success("Password reset");
      setResetting(null); setNewPwd("");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const toggle = async (emp) => {
    try {
      if (emp.status === "active") {
        await api.delete(`/admin/employees/${emp.id}`);
      } else {
        await api.patch(`/admin/employees/${emp.id}`, { status: "active" });
      }
      load(page);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  return (
    <Shell role="admin">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Employees</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Manage MRs & Admins</h1>
        </div>
        <Dialog open={adding} onOpenChange={setAdding}>
          <div className="flex gap-2">
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
            <Button variant="outline" className="h-11 gap-2" onClick={() => fileInputRef.current?.click()} disabled={importing}>
              <FileSpreadsheet className="w-4 h-4" />{importing ? "Importing…" : "Import Excel"}
            </Button>
            <DialogTrigger asChild>
              <Button className="h-11 bg-brand-600 hover:bg-brand-700 gap-2" data-testid="btn-add-employee"><Plus className="w-4 h-4"/>Add Employee</Button>
            </DialogTrigger>
          </div>
          <DialogContent>
            <DialogHeader><DialogTitle>Add new employee</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[
                ["emp_code", "EMP Code *"],
                ["name", "Full Name *"],
                ["password", "Initial Password *"],
                ["email", "Email"],
                ["mobile", "Mobile"],
                ["region", "Region"],
              ].map(([k, l]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">{l}</Label>
                  <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} type={k === "password" ? "text" : "text"} data-testid={`emp-input-${k}`} />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-600">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="emp-input-role"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mr">Medical Representative</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
              <Button onClick={create} className="bg-brand-600 hover:bg-brand-700" data-testid="emp-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-3 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={onQChange} placeholder="Search by EMP code, name, email…" className="pl-9 h-11" data-testid="emp-search" />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">EMP Code</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Region</th>
                <th className="px-4 py-3">Posters</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="emp-table-body">
              {employees.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`emp-row-${e.emp_code}`}>
                  <td className="px-4 py-3 font-semibold">{e.emp_code}</td>
                  <td className="px-4 py-3">{e.name}<div className="text-xs text-slate-500">{e.email}</div></td>
                  <td className="px-4 py-3"><Badge variant={e.role === "admin" ? "default" : "secondary"}>{e.role}</Badge></td>
                  <td className="px-4 py-3 text-slate-600">{e.region || "—"}</td>
                  <td className="px-4 py-3 font-mono">{e.poster_count}</td>
                  <td className="px-4 py-3">
                    <Badge className={e.status === "active" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-200 text-slate-600 hover:bg-slate-200"}>
                      {e.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button size="icon" variant="ghost" onClick={() => setEditing({ ...e })} data-testid={`btn-edit-${e.emp_code}`}><UserCog className="w-4 h-4"/></Button>
                    <Button size="icon" variant="ghost" onClick={() => setResetting(e)} data-testid={`btn-reset-${e.emp_code}`}><KeyRound className="w-4 h-4"/></Button>
                    <Button size="icon" variant="ghost" onClick={() => toggle(e)} data-testid={`btn-toggle-${e.emp_code}`}>
                      {e.status === "active" ? <UserX className="w-4 h-4 text-rose-600"/> : <UserCheck className="w-4 h-4 text-emerald-600"/>}
                    </Button>
                  </td>
                </tr>
              ))}
              {employees.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400">No employees</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {(() => {
          const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
          return (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
              <div className="text-xs text-slate-500">
                {total} {total === 1 ? "employee" : "employees"}
                {totalPages > 1 && <> · page {page} of {totalPages}</>}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          );
        })()}
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit employee</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              {[
                ["name", "Name"], ["email", "Email"], ["mobile", "Mobile"], ["region", "Region"],
              ].map(([k, l]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">{l}</Label>
                  <Input value={editing[k] || ""} onChange={(ev) => setEditing({ ...editing, [k]: ev.target.value })} />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Role</Label>
                  <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mr">MR</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-brand-600 hover:bg-brand-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset pwd */}
      <Dialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password — {resetting?.emp_code}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-600">New password</Label>
            <Input value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Min 6 chars" data-testid="reset-input-password" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetting(null)}>Cancel</Button>
            <Button onClick={reset} className="bg-brand-600 hover:bg-brand-700" data-testid="reset-confirm">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
